import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import * as Sentry from "@sentry/react";
import ListPagination from "components/ui/ListPagination";

import { api } from "helpers/axios";
import { errorMessage, scrollToTop } from "helpers/common";
import { tableResize } from "helpers/tableResize";
import { isDbModule, moduleToBadgeClass, moduleToFallback, normalizeModule } from "helpers/module";
import ErrorAlert from "components/ui/ErrorAlert";
import TableScrollbar from "components/EmployeeManagement/TableScrollbar";
import TextTruncate from "components/ui/TextTruncate";
import { ActionMenu, Button, Input, InputAffix } from "components/ui";
import { ModuleAwareRoleIcons, roleIcons } from "pages/Settings/LoginUsersNew";
import { actionSlots, roleIconSides } from "pages/Settings/usersActionSlots";
import { useTranslation } from "translation/useTranslation";
import { getModule } from "store/userSlice";
import { getFixedFields } from "store/fieldsSlice";
import { employeeManagementPath } from "helpers/employeeDeeplink";

/**
 * The periods a person works in. The tint carries the module the period runs, so this one
 * column says what the module badges used to and names the period on top of it.
 *
 * `scope === 'all'` is not an empty list: roles are tenant-wide, so an admin reaches every
 * period without being attached to any single one.
 *
 * `translate` has to be the plain one: the tooltip is an attribute, and editableTranslate
 * returns an element that stringifies to [object Object].
 */
function PeriodBadges({ periods, scope, translate }) {
  if (scope === 'all') {
    return <div className="period-badges">
      <span className="badge-period badge-period--all" data-tooltip-id="period-all-tooltip">
        {translate('system_settings_users.periods.all', 'Alle')}
      </span>
    </div>;
  }

  if (!periods || periods.length === 0) return null;

  return <div className="period-badges">
    {
      periods.map(period => {
        const modules = (period.modules || []).map(module => normalizeModule(module, null)).filter(Boolean);
        const tint = modules.length === 1 ? moduleToBadgeClass(modules[0]) : 'multi';
        const moduleNames = modules.map(moduleToFallback).join(' / ');
        const statusName = period.status === 'draft'
          ? translate('period.status.draft', 'Entwurf')
          : translate('period.status.live', 'Aktiv');
        const tooltip = period.is_active
          ? `${period.name} - ${statusName}${moduleNames ? ` - ${moduleNames}` : ''}`
          : translate('system_settings_users.periods.removed', 'In diesem Zeitraum entfernt');

        return <span
          key={period.id}
          className={`badge-period badge-period--${tint} ${period.status === 'draft' ? 'badge-period--draft' : ''} ${period.is_active ? '' : 'badge-period--off'}`}
          data-tooltip-id="period-badge-tooltip"
          data-tooltip-content={tooltip}
        >
          {period.name}
        </span>;
      })
    }
  </div>;
}

/**
 * The roles cell. Only the icons the person actually holds are rendered, and the divider is
 * drawn only when there is something on both sides of it - a bar on its own in an otherwise
 * empty cell separates nothing.
 */
function RoleIconsCell({ user, isDbModuleActive, translate }) {
  const sides = roleIconSides(user);
  const evaluator = user.roles.some(role => role.includes('evaluator')) && user.groups_evaluating.length > 0;
  const secondaryEvaluator = user.groups_secondary_evaluating.length > 0;
  const reviewer = user.roles.some(role => role.includes('reviewer')) && user.groups_reviewing.length > 0;

  return <>
    <ModuleAwareRoleIcons roles={user.roles} isDbModuleActive={isDbModuleActive} translate={translate} allModules={true} />
    {(sides.admin || sides.group) && <span className="vr"></span>}
    <span className={`role-icon role-icon-evaluator mr-1 ${!evaluator ? 'invisible' : ''}`} data-tooltip-id="role-tooltip" data-tooltip-content={translate('roles.evaluator')}>
      <img src={roleIcons['evaluator']} alt="" />
    </span>
    <span className={`role-icon role-icon-secondary-evaluator mr-1 ${!secondaryEvaluator ? 'invisible' : ''}`} data-tooltip-id="role-tooltip" data-tooltip-content={translate('state_employee.second_evaluator')}>
      <img src={roleIcons['evaluator']} alt="" />
    </span>
    <span className={`role-icon role-icon-reviewer mr-1 ${!reviewer ? 'invisible' : ''}`} data-tooltip-id="role-tooltip" data-tooltip-content={translate('roles.reviewer')}>
      <img src={roleIcons['reviewer']} alt="" />
    </span>
  </>;
}

/**
 * One overflow menu per row. The employee entry that used to render greyed out when the person
 * is not an employee of the active period is simply absent now - the Zeitraum badges next to it
 * already say which periods they are in, which is what that tooltip was explaining.
 *
 * `translate` has to be the plain one - the menu label ends up in aria-label.
 */
function ActionCell({ user, link, translate }) {
  const { userEdit, employeeEdit } = actionSlots(user);
  const items = [];

  if (userEdit === 'link') {
    items.push({
      key: 'edit-user',
      label: translate('system_settings_users.actions.edit_user', 'Nutzende:n bearbeiten'),
      href: link(user, 'login_users'),
      target: '_blank',
    });
  }

  if (employeeEdit === 'link') {
    items.push({
      key: 'edit-employee',
      label: translate('system_settings_users.actions.edit_employee', 'Beschäftigte:n bearbeiten'),
      href: link(user, 'employees'),
      target: '_blank',
    });
  }

  return <td className="text-nowrap action-cell">
    {
      !user?.is_deleted && <ActionMenu items={items} label={translate('system_settings_users.actions.label', 'Aktionen')} />
    }
  </td>;
}

export default function Users() {
  const { translate, editableTranslate } = useTranslation();

  const currentModule = useSelector(getModule);
  const isDbModuleActive = useMemo(() => isDbModule(currentModule), [currentModule]);

  const fixedFields = useSelector(getFixedFields);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [users, setUsers] = useState([]);
  const [periods, setPeriods] = useState([]);

  const [searchValue, setSearchValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [quickFilters, setQuickFilters] = useState([]); // prefixed keys: type:/role:/module:

  const [sortCols, setSortCols] = useState([['last_name', 'asc']]);
  const [sortPinCol, setSortPinCol] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    totalRows: null,
    totalPages: 1,
    perPage: 20
  });

  const [resizedCount, setResizedCount] = useState(0);

  const tableRef = useRef(null);
  const theadRef = useRef(null);

  const fixedFieldsObj = useMemo(() => {
    const obj = {};
    for (const f of (fixedFields || [])) {
      obj[f.slug] = f;
    }
    return obj;
  }, [fixedFields]);

  const fetchUsers = async (page = null) => {
    if (!page) page = pagination.page;

    setError(null);
    setLoading(true);

    try {
      let url = `/users?roles=all_with_employees&include=employeeInfo&limit=${pagination.perPage}&page=${page}`;
      if (searchQuery) url += `&q=${encodeURIComponent(searchQuery)}`;
      if (quickFilters.length > 0) url += `&quick_filters=${quickFilters.join(',')}`;
      if (sortCols.length > 0) url += `&sort=` + sortCols.map(sc => `${sc[0]}:${sc[1]}`).join(',');

      const { data, status } = await api.get(url);
      if (status === 200) {
        setUsers(data.data);
        setPeriods(data.meta.periods || []);
        const temp = data.meta.pagination;
        setPagination({ page, totalRows: +temp.total, totalPages: temp.total_pages, perPage: +temp.per_page });
        scrollToTop();
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch users'));
      Sentry.captureException(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, [pagination.page, pagination.perPage, searchQuery, sortCols, quickFilters]);

  const minColWidth = {
    roles: 120,
    periods: 160,
    action: 60
  };

  const maxColWidth = {
    last_name: 300,
    first_name: 300,
    employee_number: 250,
    email: 300,
    roles: 200,
    periods: 320
  };

  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;

    tableResize(table, 'system_settings_users', false, minColWidth, maxColWidth, setResizedCount);
  }, [users]);

  useEffect(() => {
    setResizedCount(c => c + 1);

    const theadEl = theadRef.current;
    if (!theadEl) return;

    const handleScrollTable = () => {
      const navHeight = document.getElementById('navbar-menu')?.offsetHeight || 0;

      const theadParentCoordinates = theadEl.parentElement.getBoundingClientRect();
      if (theadParentCoordinates.y < navHeight) {
        theadEl.style.transform = `translate3d(0, ${-theadParentCoordinates.y + navHeight}px, 0)`;
      } else {
        theadEl.style.transform = 'translate3d(0,0,0)';
      }
    };
    window.addEventListener('scroll', handleScrollTable);
    return () => window.removeEventListener('scroll', handleScrollTable);
  }, [users]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination({ ...pagination, page: 1 });
    setSearchQuery(searchValue);
  };

  const handleToggleFilter = (key) => {
    setPagination({ ...pagination, page: 1 });
    setQuickFilters(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleClearFilters = () => {
    setPagination({ ...pagination, page: 1 });
    setQuickFilters([]);
  };

  const filterSections = [
    {
      key: 'type',
      label: editableTranslate('system_settings_users.filters.type', 'Typ'),
      options: [
        { key: 'type:login_users', label: editableTranslate('system_settings_users.filters.login_users', 'Nutzende') },
        { key: 'type:employee', label: editableTranslate('system_settings_users.filters.employee', 'Beschäftigte') },
      ],
    },
    {
      key: 'role',
      label: editableTranslate('system_settings_users.filters.role', 'Rolle'),
      options: [
        { key: 'role:admin', label: editableTranslate('system_settings_users.filters.admin', 'Admin') },
        { key: 'role:hr', label: editableTranslate('system_settings_users.filters.hr', 'Verwaltung') },
        { key: 'role:evaluator', label: editableTranslate('system_settings_users.filters.evaluator', 'Bewertung') },
        { key: 'role:reviewer', label: editableTranslate('system_settings_users.filters.reviewer', 'Rezension') },
      ],
    },
    {
      key: 'module',
      label: editableTranslate('system_settings_users.filters.module', 'Modul'),
      options: [
        { key: 'module:lob', label: moduleToFallback('lob') },
        { key: 'module:db', label: moduleToFallback('db') },
        { key: 'module:unassigned', label: editableTranslate('system_settings_users.filters.unassigned', 'Nicht zugewiesen') },
      ],
    },
    // one chip per live and draft period, in the order the listing renders the badges
    {
      key: 'period',
      label: editableTranslate('system_settings_users.filters.period', 'Zeitraum'),
      options: periods.map(period => ({
        key: `period:${period.id}`,
        label: period.status === 'draft'
          ? `${period.name} (${translate('period.status.draft', 'Entwurf')})`
          : period.name,
      })),
    },
    // Three states that look alike in the list. Gelöscht is the soft-deleted record, the harsh
    // deletion, and those rows are out of the listing until this chip asks for them. Im Zeitraum
    // entfernt is per period and pairs with the Zeitraum chips above: with one on it means removed
    // from that period, with none on it means removed from any live or draft period.
    {
      key: 'status',
      label: editableTranslate('system_settings_users.filters.status', 'Status'),
      options: [
        { key: 'status:active', label: editableTranslate('system_settings_users.filters.active', 'Aktiv') },
        { key: 'status:removed', label: editableTranslate('system_settings_users.filters.removed', 'Im Zeitraum entfernt') },
        { key: 'status:deleted', label: editableTranslate('system_settings_users.filters.deleted', 'Gelöscht') },
      ],
    },
  ].filter(section => section.options.length > 0);

  const getSortCol = (field) => sortCols.find(sc => sc[0] === field) || null;

  const handleChangeSortCol = (field) => {
    setPagination({ ...pagination, page: 1 });
    setSortCols(prev => {
      const next = [...prev];
      const i = next.findIndex(sc => sc[0] === field);
      if (i >= 0) {
        next[i] = [field, next[i][1] === 'asc' ? 'desc' : 'asc'];
      } else {
        if (next.length > 1 || !sortPinCol) next.pop(); // max 2 cols; without a pin keep just one
        next.push([field, 'asc']);
      }
      return next;
    });
  };

  const handleSetSortPinCol = (field) => {
    setPagination({ ...pagination, page: 1 });
    if (sortPinCol === field) {
      setSortPinCol(null);
      setSortCols(prev => [prev[0]]);
    } else {
      setSortPinCol(field);
      setSortCols(prev => prev.filter(sc => sc[0] === field));
    }
  };

  const renderSortableTh = (field, label) => {
    const sc = getSortCol(field);
    return (
      <th scope="col" className="cursor-pointer">
        <span className="float-right floating-icons">
          <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${sc ? (sc[1] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol(field)} data-testid={`sort-${field}`} />
          {
            sc && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === field ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol(field)} data-testid={`sort-pin-${field}`} />
          }
        </span>
        <span data-testid={`sort-label-${field}`} className="th-span" onClick={() => handleChangeSortCol(field)}>
          {label}
          {
            field === 'roles' && <i className={`bi bi-info-circle ml-1`} data-tooltip-id="roles-table-header-tooltip"></i>
          }
          <i className="bi bi-bold bi-arrow-up invisible ml-1"></i>
          <i className="bi bi-bold bi-pin-angle-fill invisible ml-1"></i>
        </span>
        <div className="th-resize-div" data-field={field}></div>
      </th>
    );
  };

  const employeeManagementLink = (user, tab) => {
    const staffId = user?.employeeInfo?.employee_number;
    return employeeManagementPath({ tab, ...(staffId ? { staffId } : { userId: user?.hash_id }) });
  };

  return (
    <div className="col-lg col-md-9 section-col-right d-flex">
      <div className="bg-white w-100 p-4 content-card">
        <div className="d-flex justify-content-between align-items-start">
          <div className="pr-4">
            <h4>
              {editableTranslate('global.menu.settings.users', 'Beschäftigte / Verbeamtete')}
              {pagination.totalRows !== null && <span className="ml-2">({pagination.totalRows})</span>}
            </h4>
            <p className="section-description mb-0">{editableTranslate('system_settings_users.description', 'Liste der Nutzenden und Beschäftigten aus allen Modulen.')}</p>
          </div>

          <form className="form-inline min-w-200" onSubmit={handleSearch}>
            <InputAffix className={searchValue ? 'bg-red-dark-soft' : ''} prefix={<i className="bi bi-search mr-2"></i>}>
              <Input data-testid="search-input" type="text" value={searchValue} onChange={e => setSearchValue(e.target.value)} autoComplete="false" placeholder={translate('form.search')} maxLength={255} />
            </InputAffix>
          </form>
        </div>

        <div className="users-quick-filters d-flex flex-wrap mt-4">
          {
            filterSections.map(section => (
              <div key={section.key} className="mr-4 mb-2">
                <div className="font-weight-bold mb-1">{section.label}</div>
                <div className="d-flex flex-wrap gap-2">
                  {
                    section.options.map(opt => {
                      const active = quickFilters.includes(opt.key);
                      return (
                        <div data-testid={`quick-filter-${opt.key}`} key={opt.key} onClick={() => handleToggleFilter(opt.key)} className={`statistic-chooseable-badge ${active ? 'active' : ''}`}>
                          <span>{opt.label}</span>
                          <span className="ml-1">
                            {active ? <i className="bi bi-x-circle-fill"></i> : <i className="bi bi-plus"></i>}
                          </span>
                        </div>
                      );
                    })
                  }
                </div>
              </div>
            ))
          }
        </div>

        <div className="mt-3">
          {
            error && <div className="mb-3">
              <ErrorAlert error={error} onClose={() => setError(null)} />
            </div>
          }

          <div className="table-responsive has-custom-scrollbar">
            <table ref={tableRef} className="table common-table system-settings-users-table" data-testid="users-table">
              <thead ref={theadRef} className="sticky-top">
                <tr>
                  {renderSortableTh('last_name', fixedFieldsObj?.last_name?.custom_name || editableTranslate('field.last_name'))}
                  {renderSortableTh('first_name', fixedFieldsObj?.first_name?.custom_name || editableTranslate('field.first_name'))}
                  {renderSortableTh('employee_number', fixedFieldsObj?.employee_number?.custom_name || editableTranslate('field.employee_number'))}
                  {renderSortableTh('email', fixedFieldsObj?.email?.custom_name || editableTranslate('field.email'))}
                  {renderSortableTh('roles', editableTranslate('global.menu.settings.roles'))}
                  <th scope="col">
                    {editableTranslate('system_settings_users.periods', 'Zeiträume')}
                    <i className="bi bi-info-circle ml-1" data-tooltip-id="periods-table-header-tooltip"></i>
                    <div className="th-resize-div" data-field={'periods'}></div>
                  </th>
                  <th scope="col" className="min-w-60">
                    <div className="th-resize-div" data-field={'action'}></div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {
                  !loading && users.map((user, index) => <tr key={index} className={`tr-hover table-row no-select ${user.is_deleted ? 'row-deleted' : ''}`} data-testid={`row-${user?.hash_id}`}>
                    <td><TextTruncate text={user.last_name} resizedCount={resizedCount} /></td>
                    <td><TextTruncate text={user.first_name} resizedCount={resizedCount} /></td>
                    <td><TextTruncate text={user?.employeeInfo?.employee_number} resizedCount={resizedCount} /></td>
                    <td><TextTruncate text={user.email} resizedCount={resizedCount} /></td>
                    <td className="roles-cell text-nowrap">
                      <RoleIconsCell user={user} isDbModuleActive={isDbModuleActive} translate={translate} />
                    </td>
                    <td className="periods-cell">
                      <PeriodBadges periods={user.periods} scope={user.periods_scope} translate={translate} />
                    </td>
                    <ActionCell user={user} link={employeeManagementLink} translate={translate} />
                  </tr>)
                }
                {
                  loading && <tr>
                    <td colSpan={7}>
                      <div className="d-flex justify-content-center" data-testid="loading">
                        <div className="spinner-border" role="status">
                          <span className="sr-only">Wird geladen...</span>
                        </div>
                      </div>
                    </td>
                  </tr>
                }
                {
                  !loading && users.length === 0 && <tr>
                    <td colSpan={7} className="text-center" data-testid="empty-state">{editableTranslate('global.no_data')}</td>
                  </tr>
                }
              </tbody>
            </table>
            <ReactTooltip id="role-tooltip" place="bottom" />
            <ReactTooltip id="period-badge-tooltip" place="bottom" />
            <ReactTooltip id="period-all-tooltip" place="bottom" clickable={true} content={translate('system_settings_users.periods.all_tooltip', 'Diese Rolle gilt für alle Bewertungszeiträume.')} />
            <ReactTooltip id="roles-table-header-tooltip" place="bottom" clickable={true} content={translate('system_settings_users.roles_table_header_tooltip', 'Rollen werden aus allen Perioden zusammengeführt.')} />
            <ReactTooltip id="periods-table-header-tooltip" place="bottom" clickable={true} content={translate('system_settings_users.periods_table_header_tooltip', 'Bewertungszeiträume, in denen die Person arbeitet. Die Farbe zeigt das Modul des Zeitraums. Ein ausgegrauter Zeitraum bedeutet, dass die Person dort entfernt wurde.')} />
          </div>
          <TableScrollbar tableRef={tableRef} data={users} trigger={resizedCount} />

          <ListPagination
            page={pagination.page}
            perPage={pagination.perPage}
            totalPages={pagination.totalPages}
            totalRows={pagination.totalRows}
            setPage={(page) => setPagination({ ...pagination, page })}
            setPerPage={(perPage) => setPagination({ ...pagination, page: 1, perPage })}
            className="mt-4"
          />
        </div>
      </div>
    </div>
  );
}
